import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material'
import {
  Restaurant,
  Logout,
  Assignment,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  PictureAsPdf,
  TableChart
} from '@mui/icons-material'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/axios'

const toKey = (id) => String(id)
const makeKey = (editalId, alimentoId) => `${editalId}-${alimentoId}`

const LIMITE_ANUAL_FORNECIMENTO = 40000

const formatBRL = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

// Entidade responsável pelo limite anual: o grupo/associação do produtor, se houver, senão o próprio produtor.
const getEntidadeKey = (concorrente) => {
  if (!concorrente) return null
  return concorrente.idgrupo ? `grupo:${concorrente.idgrupo}` : `produtor:${concorrente.produtor_id}`
}

const getEntidadeNome = (concorrente) => {
  if (!concorrente) return ''
  return concorrente.idgrupo ? `Grupo/associação de ${concorrente.nome_produtor}` : concorrente.nome_produtor
}

export default function EditaisProdutosPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [editais, setEditais] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [expanded, setExpanded] = useState({})
  const [alimentosByEdital, setAlimentosByEdital] = useState({})
  const [loadingAlimentos, setLoadingAlimentos] = useState({})
  const [concorrentesByEdital, setConcorrentesByEdital] = useState({})
  const [loadingConcorrentes, setLoadingConcorrentes] = useState({})
  const [atribByEdital, setAtribByEdital] = useState({})
  const [atribCarregadaByEdital, setAtribCarregadaByEdital] = useState({})

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true)
        const res = await api.get('/editais/list.php')
        if (res.data.success) setEditais(res.data.editais || [])
        else setError(res.data.message || 'Erro ao carregar editais')
      } catch {
        setError('Erro ao carregar editais. Tente novamente.')
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [])

  const logoutAdmin = () => {
    logout()
    navigate('/admin/login')
  }

  const isFinalizado = (edital) => edital.status === 'finalizado'

  const carregarAlimentos = async (editalId) => {
    if (alimentosByEdital[editalId]) return alimentosByEdital[editalId]
    setLoadingAlimentos((p) => ({ ...p, [editalId]: true }))
    try {
      const res = await api.put('/editaisprodutos/list.php', { idedital: editalId })
      const alimentos = res.data.success ? (res.data.alimentos || []) : []
      setAlimentosByEdital((p) => ({ ...p, [editalId]: alimentos }))
      return alimentos
    } catch {
      setError('Erro ao carregar produtos do edital.')
      return []
    } finally {
      setLoadingAlimentos((p) => ({ ...p, [editalId]: false }))
    }
  }

  const toggleEdital = async (editalId) => {
    const open = !expanded[editalId]
    setExpanded((p) => ({ ...p, [editalId]: open }))
    if (open) {
      await Promise.all([
        carregarAlimentos(editalId),
        carregarConcorrentes(editalId),
        carregarAtribuicoesSalvas(editalId)
      ])
    }
  }

  // Carrega do banco as atribuições (produtor + quantidade) já salvas para o edital,
  // preenchendo os selects/campos de quantidade ao reabrir a tela.
  const carregarAtribuicoesSalvas = async (editalId) => {
    if (atribCarregadaByEdital[editalId]) return
    try {
      const res = await api.get(`/editais/atribuicoes.php?edital_id=${editalId}`)
      const atribuicoesSalvas = res.data.success ? (res.data.atribuicoes || {}) : {}
      setAtribByEdital((prev) => ({
        ...prev,
        [editalId]: { ...atribuicoesSalvas, ...(prev[editalId] || {}) }
      }))
    } catch {
      setError('Erro ao carregar atribuições salvas do edital.')
    } finally {
      setAtribCarregadaByEdital((p) => ({ ...p, [editalId]: true }))
    }
  }

  const carregarConcorrentes = async (editalId) => {
    if (concorrentesByEdital[editalId]) return concorrentesByEdital[editalId]
    setLoadingConcorrentes((p) => ({ ...p, [editalId]: true }))
    try {
      const res = await api.get(`/editais/concorrentes.php?edital_id=${editalId}`)
      const lista = res.data.success ? (res.data.concorrentes || []) : []
      setConcorrentesByEdital((p) => ({ ...p, [editalId]: lista }))
      return lista
    } catch {
      setError('Erro ao carregar concorrentes do edital.')
      return []
    } finally {
      setLoadingConcorrentes((p) => ({ ...p, [editalId]: false }))
    }
  }

  const handleUpdateAtribuicao = (editalId, alimentoId, field, value) => {
    setAtribByEdital((prev) => {
      const editalAtribs = prev[editalId] || {}
      const currentAtrib = editalAtribs[alimentoId] || { concorrente_id: null, quantidade: 0 }
      
      return {
        ...prev,
        [editalId]: {
          ...editalAtribs,
          [alimentoId]: {
            ...currentAtrib,
            [field]: value
          }
        }
      }
    })
  }

  const resumo = useMemo(() => {
    const r = {}
    editais.forEach((e) => {
      const alimentos = alimentosByEdital[e.id] || []
      const mapa = atribByEdital[e.id] || {}
      
      let atribuidosCount = 0
      alimentos.forEach(al => {
        const atrib = mapa[al.id]
        if (atrib && atrib.concorrente_id && atrib.quantidade > 0) {
          atribuidosCount++
        }
      })

      r[e.id] = {
        totalAlimentos: alimentos.length,
        totalAtribuidos: atribuidosCount,
        atribuicoes: mapa
      }
    })
    return r
  }, [editais, alimentosByEdital, atribByEdital])

  // Para cada edital, consolida por entidade (grupo/associação ou produtor individual) o valor
  // já comprometido em outros editais (total_fornecido_ano) somado ao valor sendo atribuído
  // agora neste edital, produto a produto — para checar o limite anual de R$ 40.000,00.
  const entidadeInfoByEdital = useMemo(() => {
    const out = {}
    editais.forEach((e) => {
      const alimentos = alimentosByEdital[e.id] || []
      const mapa = atribByEdital[e.id] || {}
      const concorrentes = concorrentesByEdital[e.id] || []
      const concorrentesById = {}
      concorrentes.forEach((c) => { concorrentesById[toKey(c.id)] = c })

      const entidades = {}
      alimentos.forEach((al) => {
        const atrib = mapa[al.id]
        if (!atrib || !atrib.concorrente_id) return
        const concorrente = concorrentesById[toKey(atrib.concorrente_id)]
        const key = getEntidadeKey(concorrente)
        if (!key) return
        if (!entidades[key]) {
          entidades[key] = {
            existente: Number(concorrente.total_fornecido_ano) || 0,
            nome: getEntidadeNome(concorrente),
            porAlimento: {}
          }
        }
        const valorItem = (Number(atrib.quantidade) || 0) * (Number(al.valor) || 0)
        entidades[key].porAlimento[al.id] = valorItem
      })

      out[e.id] = { entidades, concorrentesById }
    })
    return out
  }, [editais, alimentosByEdital, atribByEdital, concorrentesByEdital])

  // Total (existente + o que está sendo atribuído neste edital) de uma entidade, opcionalmente
  // ignorando a contribuição de um alimento específico (para simular "e se eu escolher X aqui").
  const totalEntidadeExcluindo = (editalId, entidadeKey, alimentoIdExcluir) => {
    const info = entidadeInfoByEdital[editalId]
    if (!info || !entidadeKey || !info.entidades[entidadeKey]) return 0
    const { existente, porAlimento } = info.entidades[entidadeKey]
    let novo = 0
    Object.entries(porAlimento).forEach(([alId, valor]) => {
      if (toKey(alId) !== toKey(alimentoIdExcluir)) novo += valor
    })
    return existente + novo
  }

  const temExcedenteByEdital = useMemo(() => {
    const out = {}
    Object.entries(entidadeInfoByEdital).forEach(([editalId, info]) => {
      out[editalId] = Object.values(info.entidades).some((en) => {
        const total = en.existente + Object.values(en.porAlimento).reduce((s, v) => s + v, 0)
        return total > LIMITE_ANUAL_FORNECIMENTO
      })
    })
    return out
  }, [entidadeInfoByEdital])

  const finalizar = async (edital) => {
    const info = resumo[edital.id] || { totalAlimentos: 0, totalAtribuidos: 0, atribuicoes: {} }
    if (info.totalAlimentos === 0) return setError('Este edital não possui produtos cadastrados.')
    if (info.totalAtribuidos !== info.totalAlimentos) {
      return setError('Atribua todos os produtos com produtor e quantidade antes de finalizar.')
    }
    if (temExcedenteByEdital[edital.id]) {
      return setError('Não é possível finalizar: algum produtor ou grupo/associação ultrapassaria o limite anual de R$ 40.000,00. Ajuste as quantidades.')
    }
    try {
      // Obter lista única de produtores que foram selecionados para pelo menos um produto
      const produtoresIds = [...new Set(
        Object.values(info.atribuicoes)
          .map(a => a.concorrente_id)
          .filter(Boolean)
      )]

      const res = await api.post('/editais/finalizar.php', {
        edital_id: edital.id,
        produtores_selecionados: produtoresIds,
        atribuicoes_produtos: info.atribuicoes
      })
      if (res.data.success) {
        setSuccess('Edital finalizado com sucesso!')
        // Atualizar o status localmente
        setEditais(prev => prev.map(e => e.id === edital.id ? { ...e, status: 'finalizado' } : e))
        setTimeout(() => setSuccess(''), 3000)
      } else setError(res.data.message || 'Erro ao finalizar edital')
    } catch (e) {
      setError(e.response?.data?.message || 'Erro ao finalizar edital')
    }
  }

  const exportarExcel = async (edital) => {
    const alimentos = alimentosByEdital[edital.id] || []
    const atribs = resumo[edital.id]?.atribuicoes || {}
    const wb = XLSX.utils.book_new()
    // XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{ Titulo: edital.titulo, Status: edital.status }]), 'Edital')
    
    const rows = alimentos.map(al => {
      const atrib = atribs[al.id] || {}
      const concorrente = (concorrentesByEdital[edital.id] || []).find(c => c.id === atrib.concorrente_id)
      return {
        Produto: al.nome,
        Produtor: concorrente ? concorrente.nome_produtor : 'Não atribuído',
        Quantidade: atrib.quantidade || 0,
        Unidade: al.unidademedida || ''
      }
    })

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ Mensagem: 'Sem dados' }]), 'Atribuições')
    XLSX.writeFile(wb, `edital_${edital.id}_atribuicoes.xlsx`)
  }

  const exportarPdf = async (edital) => {
    const alimentos = alimentosByEdital[edital.id] || []
    const atribs = resumo[edital.id]?.atribuicoes || {}
    const doc = new jsPDF()
    doc.text(`Edital #${edital.id} - ${edital.titulo || ''}`, 14, 12)
    
    const body = alimentos.map(al => {
      const atrib = atribs[al.id] || {}
      const concorrente = (concorrentesByEdital[edital.id] || []).find(c => c.id === atrib.concorrente_id)
      return [
        al.nome,
        concorrente ? concorrente.nome_produtor : 'Não atribuído',
        atrib.quantidade || 0,
        al.unidademedida || ''
      ]
    })

    autoTable(doc, {
      startY: 20,
      head: [['Produto', 'Produtor', 'Quantidade', 'Unidade']],
      body: body
    })
    
    doc.save(`edital_${edital.id}_atribuicoes.pdf`)
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Agroeconomia - Editais x Produtos</Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>{user?.nome}</Typography>
          <IconButton color="inherit" onClick={logoutAdmin}><Logout /></IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, mt: 3, fontWeight: 600 }}>Atribuição de produtores por produto</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : editais.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">Nenhum edital cadastrado</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editais.map((edital) => {
              const info = resumo[edital.id] || { totalAlimentos: 0, totalAtribuidos: 0 }
              const open = !!expanded[edital.id]
              const finalizado = isFinalizado(edital)
              const alimentos = alimentosByEdital[edital.id] || []
              const concorrentes = concorrentesByEdital[edital.id] || []
              const atribs = atribByEdital[edital.id] || {}

              return (
                <Card key={edital.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="h6">{edital.titulo}</Typography>
                        <Typography variant="body2" color="text.secondary">{edital.descricao}</Typography>
                      </Box>
                      <IconButton onClick={() => toggleEdital(edital.id)}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Typography variant="body2"><strong>Atribuídos:</strong> {info.totalAtribuidos}/{info.totalAlimentos}</Typography>
                      {finalizado && <Typography variant="body2" color="success.main"><strong>Finalizado</strong></Typography>}
                    </Box>

                    {open && (
                      <Box>
                        {loadingAlimentos[edital.id] || loadingConcorrentes[edital.id] ? (
                          <CircularProgress size={28} />
                        ) : (
                          <>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                              <Button size="small" variant="outlined" startIcon={<TableChart />} onClick={() => exportarExcel(edital)}>Excel</Button>
                              <Button size="small" variant="outlined" color="secondary" startIcon={<PictureAsPdf />} onClick={() => exportarPdf(edital)}>PDF</Button>
                            </Box>

                            {!finalizado && temExcedenteByEdital[edital.id] && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                Existe produtor ou grupo/associação que ultrapassaria o limite anual de R$ 40.000,00 em fornecimento. Reduza as quantidades atribuídas para poder finalizar.
                              </Alert>
                            )}

                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                              <Table size="small">
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Produtor</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {alimentos.map((al) => {
                                    const atrib = atribs[al.id] || { concorrente_id: '', quantidade: 0 }
                                    const concorrenteSelecionado = (concorrentesByEdital[edital.id] || [])
                                      .find((c) => toKey(c.id) === toKey(atrib.concorrente_id))
                                    const entidadeKey = getEntidadeKey(concorrenteSelecionado)
                                    const valorEsteItem = (Number(atrib.quantidade) || 0) * (Number(al.valor) || 0)
                                    const totalSemEsteItem = totalEntidadeExcluindo(edital.id, entidadeKey, al.id)
                                    const totalComEsteItem = totalSemEsteItem + valorEsteItem
                                    const excedeuLimite = !!entidadeKey && totalComEsteItem > LIMITE_ANUAL_FORNECIMENTO

                                    return (
                                      <TableRow key={al.id}>
                                        <TableCell>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{al.nome}</Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Req: {al.quantidade} {al.unidademedida}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <FormControl fullWidth size="small" error={excedeuLimite}>
                                            <Select
                                              value={atrib.concorrente_id || ''}
                                              onChange={(e) => handleUpdateAtribuicao(edital.id, al.id, 'concorrente_id', e.target.value)}
                                              disabled={finalizado}
                                              displayEmpty
                                            >
                                              <MenuItem value=""><em>Selecione um produtor</em></MenuItem>
                                              {concorrentes.map((c) => {
                                                const key = getEntidadeKey(c)
                                                const atingiuLimite = totalEntidadeExcluindo(edital.id, key, al.id) >= LIMITE_ANUAL_FORNECIMENTO
                                                const ehEsteProdutor = toKey(atrib.concorrente_id) === toKey(c.id)
                                                return (
                                                  <MenuItem key={c.id} value={c.id} disabled={atingiuLimite && !ehEsteProdutor}>
                                                    {c.nome_produtor} ({c.cpf}){atingiuLimite ? ' — limite anual atingido' : ''}
                                                  </MenuItem>
                                                )
                                              })}
                                            </Select>
                                          </FormControl>
                                        </TableCell>
                                        <TableCell>
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={atrib.quantidade || ''}
                                            onChange={(e) => handleUpdateAtribuicao(edital.id, al.id, 'quantidade', parseFloat(e.target.value) || 0)}
                                            disabled={finalizado}
                                            error={excedeuLimite}
                                            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                                            sx={{ width: '140px' }}
                                          />
                                          {entidadeKey && !(Number(al.valor) > 0) && (
                                            <Typography
                                              variant="caption"
                                              color="warning.main"
                                              sx={{ display: 'block', mt: 0.5 }}
                                            >
                                              Produto sem valor unitário cadastrado — limite anual não verificado para este item.
                                            </Typography>
                                          )}
                                          {entidadeKey && Number(al.valor) > 0 && (
                                            <Typography
                                              variant="caption"
                                              color={excedeuLimite ? 'error' : 'text.secondary'}
                                              sx={{ display: 'block', mt: 0.5 }}
                                            >
                                              {excedeuLimite
                                                ? `Limite anual de R$ 40.000,00 excedido (total ${formatBRL(totalComEsteItem)})`
                                                : `Ano: ${formatBRL(totalComEsteItem)} / ${formatBRL(LIMITE_ANUAL_FORNECIMENTO)}`}
                                            </Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  })}
                                  {alimentos.length === 0 && (
                                    <TableRow>
                                      <TableCell colSpan={3} align="center">Nenhum produto cadastrado neste edital.</TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            {!finalizado && (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CheckCircle />}
                                disabled={temExcedenteByEdital[edital.id]}
                                onClick={() => finalizar(edital)}
                              >
                                Finalizar edital ({info.totalAtribuidos}/{info.totalAlimentos})
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        )}
      </Container>
    </Box>
  )
}
